import { useState } from 'react';
import { DropSpot, HintAlert } from '@epam/promo';
import { ReactComponent as FileIcon } from '@epam/assets/icons/common/file-file_image-24.svg';
import { ReactComponent as CloseIcon } from '@epam/assets/icons/common/navigation-close-12.svg';
import { ReactComponent as NotificationIcon } from '@epam/assets/icons/common/notification-info-fill-24.svg';

import { Attachment } from 'interfaces';
import css from './FileLoader.module.scss';

const FILES_LIMIT = 10;
const SIZE_1_MB = 1_048_576;
const MAX_FILE_SIZE_5_MB = 5_242_880;
const ALLOWABLE_FILE_TYPES = ['image/png', 'image/jpeg'];

interface FileLoaderProps<T extends Attachment> {
  attachments: T[];
  isReadonly: boolean;
  setDraftPictures: (files: T[]) => void;
}

export const FileLoader = <T extends Attachment>({
  attachments = [],
  isReadonly = false,
  setDraftPictures,
}: FileLoaderProps<T>) => {
  const [isHintShown, setIsHintShown] = useState(false);

  const deleteFile = (index: number) => {
    const newFilesList = attachments.filter((_, i) => index !== i);

    setIsHintShown(false);
    setDraftPictures(newFilesList);
  };

  const uploadFile = (files: File[]) => {
    setIsHintShown(false);

    if (attachments.length === FILES_LIMIT) {
      setIsHintShown(true);

      return;
    }

    const filteredFiles = files.filter(
      (f) =>
        ALLOWABLE_FILE_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE_5_MB,
    );

    const newFiles = filteredFiles.splice(0, FILES_LIMIT - attachments.length);

    if (newFiles.length < files.length) setIsHintShown(true);

    // Freshly dropped items are real `File`s, which are valid `Attachment`s.
    setDraftPictures([...attachments, ...newFiles] as T[]);
  };

  return (
    <div
      className={`${css.container} ${isReadonly ? css.containerDisabled : ''}`}>
      {!isReadonly && (
        <DropSpot
          infoText={`Up to ${FILES_LIMIT} files. Limit for 1 file is 5 MB.`}
          onUploadFiles={uploadFile}
        />
      )}
      {isHintShown && (
        <HintAlert cx={css.hint_alert} icon={NotificationIcon}>
          <div className={css.hint__text}>
            Some of your files do not meet requirements. Please try another
            file.
          </div>
        </HintAlert>
      )}
      <div className={css.attachmentBlock}>
        {attachments.map((file, i) => (
          <div key={i} className={css.fileBlock}>
            <FileIcon fill="var(--uui-orange-50)" />

            <div className={css.fileInfo}>
              <p>{file.name}</p>
              <p>
                {file.type.split('/')[1]}, {(file.size / SIZE_1_MB)?.toFixed(2)}{' '}
                MB
              </p>
            </div>
            <button
              className={css.closeIcon}
              disabled={isReadonly}
              type="button"
              onClick={() => deleteFile(i)}>
              <CloseIcon fill="var(--uui-neutral-60)" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
